import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { taskmanagementschema } from "../../../../../JsonData/CreateTaskConfig";
import { toast } from "react-toastify";
import { ExecuteSopsConfig } from "../../ExecuteSopsForm/ExecuteSopsConfig";
import "./SopsInstanceForm.css";
import { useTranslation } from "react-i18next";
import {
  getData,
  postDataWithBody,
} from "../../../../../Services/Services";
import {
  getTaskConfigsURL,
  getAllActiveRole,
  findTaskById,
} from "../../../../../Services/EndPoints";
import Loader from "react-spinner-loader";
import TaskModalWithCommentSopCancel from "../../../../../Components/UiComponents/TaskModalWithComment/TaskModalWithCommentSopCancel";
import ConfirmpopupSop from "../../../Common/ConfirmPopup/ConfirmpopupSop";

/**
 * SopsInstanceForm Component
 * 
 * This component manages SOP (Standard Operating Procedure) instances.
 * It provides functionality to view, edit, close, and cancel SOP instances.
 */
const SopsInstanceForm = (props) => {
  // ============================================================================
  // STATE DECLARATIONS
  // ============================================================================
  
  const [CreateTaskConfig, setCreateTaskConfig] = useState(ExecuteSopsConfig);
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showPop, setShowPop] = React.useState(false);
  const [showConfirmPopup, setshowConfirmPopup] = React.useState(false);
  const { state } = useLocation();
  const [formData, setFormData] = useState(null);
  const [id, setId] = useState(null);
  const SESSION_STORAGE_KEY = "sops_instance_form_data";

  /**
   * Effect to initialize component data from state or session storage
   * Handles data persistence across page refreshes and navigation
   */
  useEffect(() => {
    // First, try to get data from route state
    let stateData = state?.data;
   
    if (stateData) {
      // If state data exists, save it to session storage for persistence
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateData));
      setFormData(stateData);
      setId(stateData.id);
    } else {
      // If no state data, try to retrieve from session storage
      const storedData = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setFormData(parsedData);
        setId(parsedData.id);
      } else {
        // No data available - redirect to list view with error message
        toast.error("No SOP instance data found. Redirecting to view page.", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        setTimeout(() => navigate("../viewsops-instance"), 2000);
      }
    }
  }, [state, navigate]);


  
  /**
   * React Hook Form configuration
   * Includes validation schema, form state management, and form methods
   */
  const {
    reset,         // Reset form to default values
    formState: { }, // Form validation errors
  } = useForm({
    resolver: yupResolver(taskmanagementschema[""]),
    mode: "all", // Validate on all events
  });


  /**
   * Initialize component data when formData is available
   */
  useEffect(() => {
    if (formData) {
      getInitData();
    }
  }, [formData]);

  /**
   * Fetch view data and populate form for editing if ID exists
   */
  async function getViewData() {
    if (id) {
      await populateViewFormFrEdit(id);
    }
  }

  async function populateViewFormFrEdit(id) {
    var viewData = await postDataWithBody(findTaskById, [id], {});
    viewData = viewData[0]; // Get first result
    reset(viewData); // Reset form with fetched data
  }

  /**
   * Initialize all required data for the component
   * Fetches task configurations, roles, and view data
   */
  async function getInitData() {
    setLoader(true);
    await getTaskConfigs();
    await fetchAllRoles();
    // await getTaskStatus(); // Commented out - unused
    await getViewData();
    setLoader(false);
    if (id) {
      populateViewFormFrEdit(id);
    }
  }

  /**
   * Fetch all active roles and populate dropdown options
   * Updates the CreateTaskConfig with role options
   */
  async function fetchAllRoles() {
    let rolearr = await getData(getAllActiveRole);
    
    // Transform roles data for dropdown component
    let roleFrDropdown = [];
    for (let role in rolearr) {
      roleFrDropdown.push({
        label: rolearr[role].name,
        value: rolearr[role].id,
      });
    }
    
    // Update configuration with role options
    CreateTaskConfig[0].fields[2]["options"] = roleFrDropdown;
    setCreateTaskConfig(CreateTaskConfig);
  }

  /**
   * Fetch task configurations and populate dropdown options
   * Handles multiple configuration categories (priority, status, type, etc.)
   */
  async function getTaskConfigs() {
    let res = await postDataWithBody(
      getTaskConfigsURL,
      ["TSK_PRIOR", "INCDNT_CAT", "TSK_STAT", "TSK_TYP"],
      {}
    );
    
    // Group configurations by category
    let config_type = {};
    for (let config in res) {
      if (config_type[res[config].category_name]) {
        config_type[res[config].category_name].push({
          label: res[config].value,
          value: res[config].id,
        });
      } else {
        config_type[res[config].category_name] = [
          { label: res[config].value, value: res[config].id },
        ];
      }
    }
    
    // Update form configuration with fetched options
    CreateTaskConfig[0].fields[0]["options"] = config_type["INCDNT_CAT"];
    CreateTaskConfig[0].fields[2]["options"] = config_type["TSK_PRIOR"];
    CreateTaskConfig[0].fields[3]["options"] = config_type["TSK_STAT"];
    CreateTaskConfig[0].fields[3]["options"] = config_type["TSK_TYP"];
    setCreateTaskConfig(CreateTaskConfig);
  }

  /**
   * Show confirmation popup for closing SOP
   */
  const handleClickClose = () => {
    setshowConfirmPopup(true);
  };

  /**
   * Show modal for canceling SOP with comment
   */
  const handleclickCancel = async () => {
    setShowPop(true);
  };
  // ============================================================================
  // FILE HANDLING
  // ============================================================================
 
  
  /**
   * Show loader while form data is being loaded
   */
  if (!formData) {
    return <Loader show={true} type="body" stack="vertical" message="Loading Data" />;
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  return (
    <>
      <div className="mt-4">
        {/* Global loader for API operations */}
        <Loader
          show={loader}
          type="body"
          stack="vertical"
          message="Loading Data"
        />

        <div className="form-body">
          <div className="registration__form--body">
            {/* SOP Instance Information Display */}
            <div className="sop-instance-table">
              {/* Header Row */}
              <div className="row">
                <div className="col-sm-3">
                  <div className="sop_instance_info">ID</div>
                </div>
                <div className="col-sm-3">
                  <div className="sop_instance_info">Instance Name</div>
                </div>
                <div className="col-sm-3">
                  <div className="sop_instance_info">Status</div>
                </div>
                <div className="col-sm-3">
                  <div className="sop_instance_info">Incident</div>
                </div>
              </div>
              
              {/* Data Row 1 */}
              <div className="row">
                <div className="col-sm-3">
                  <div>{formData?.id || "N/A"}</div>
                </div>
                <div className="col-sm-3">
                  <div>{formData?.name || "N/A"}</div>
                </div>
                <div className="col-sm-3">
                  <div>{formData?.status || "N/A"}</div>
                </div>
                <div className="col-sm-3">
                  <div>{formData?.incident || "N/A"}</div>
                </div>
              </div>
              
              {/* Header Row 2 */}
              <div className="row">
                <div className="col-sm-3">
                  <div className="mt-3 sop_instance_info">Time</div>
                </div>
                <div className="col-sm-3">
                  <div className="mt-3 sop_instance_info">SOP Name</div>
                </div>
                <div className="col-sm-3" >
                  <div className="mt-3 sop_instance_info">SOP Description</div>
                </div>
                <div className="col-sm-3">
                  <div className="mt-3 sop_instance_info"></div>
                </div>
              </div>
              
              {/* Data Row 2 */}
              <div className="row">
                <div className="col-sm-3">
                  {/* Format date and time display */}
                  <div>{formData?.created_on ? (
                    new Date(formData.created_on).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }).replace(/\//g, '-') + ' ' +
                    new Date(formData.created_on).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  ) : 'N/A'}</div>
                </div>
                <div className="col-sm-3">
                  <div>{formData?.sop || "N/A"}</div>
                </div>
                <div className="col-sm-3" style={{width: "536px"}}>
                  <div>{formData?.description || "N/A"}</div>
                </div>
                <div className="col-sm-3">
                  <div></div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-4 btn-margin">
                {/* Close SOP Button - conditional styling and enabling based on status */}
                <button
                  className={`btn ${
                    formData?.status === "Closed"
                      ? "btn-closed-sop"
                      : "btn-close-sop"
                  }`}
                  style={{
                    backgroundColor:
                      formData?.status === "Closed" ? "#7cdb90" : "blue",
                    border: "none",
                    width: "121px"
                  }}
                  onClick={handleClickClose}
                  disabled={formData?.status !== "Completed"} // Only enabled when status is "Completed"
                >
                  {formData?.status === "Closed" ? "Closed" : "Close SOP"}
                </button>
                
                {/* Cancel SOP Button - only enabled for Started/In-Progress status */}
                <button
                  className="btn btn-close-sops"
                  onClick={handleclickCancel}
                  disabled={
                    formData?.status !== "Started" && formData?.status !== "In-Progress"
                  }
                  style={{height: "35px"}}
                >
                  Cancel SOP
                </button>
              </div>
              
              {/* Cancel SOP Modal */}
              {showPop && (
                <TaskModalWithCommentSopCancel
                  titleValue={t("Cancel SOP Instance")}
                  show={showPop}
                  setShowPop={setShowPop}
                  taskData={formData}
                />
              )}
              
              {/* Close SOP Confirmation Popup */}
              {showConfirmPopup && (
                <ConfirmpopupSop
                  titleValue={t("Close SOP Instance")}
                  show={showConfirmPopup}
                  setshowConfirmPopup={setshowConfirmPopup}
                  taskData={formData}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SopsInstanceForm;